import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  BookOpen,
  School,
  School2,
  GraduationCap,
  Omega,
  Star,
  Target,
  Lightbulb,
  Users,
  Award,
  BriefcaseBusiness,
  Handshake,
  MapPin,
  Globe,
} from 'lucide-react';

import useMetaTags from './UseMetaTags';
import BookingPopup from './BookingPopup';
import ReviewsCarousel from './GoogleReviews';
import FAQSection from './FAQSection';
import SEOHead from './SEOHead';

import ProfilePic from '../profilepic.webp';
import bgImage from '../new-background.webp';
import Logo from '../assets/logo.webp';
import AboutUsImg from '../assets/AboutUs.webp'


export const ModernCareerWebsite = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const [isScrolled, setIsScrolled] = useState(false);

  useMetaTags(
    'Career Counselling in Bangalore, Jamshedpur & Ranchi | Nirvana Career Counselling',
    'Expert career counselling services in Bangalore, Jamshedpur & Ranchi. Discover career paths and assessments tailored for success.',
    'career counselling Jamshedpur, career guidance Jamshedpur, career assessments Jamshedpur, career counselling Ranchi, career guidance Ranchi, career assessments Ranchi, career counselling Bangalore, career guidance Bangalore, career assessments Bangalore'
  );


  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 60) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
    <SEOHead canonicalUrl="https://nirvanacareercounselling.com" />
    <div className="min-h-screen bg-gray-50 font-lato">
    {/* Header */}
    <header className={`fixed w-full top-0 z-50 transition-all duration-300 font-lato ${
      isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
    }`}>
      <div className="w-full px-4 lg:px-12">
        <div className="max-w-[1920px] mx-auto">
          <div className="flex items-center h-20 lg:h-24">
            <div className="flex-shrink-0 lg:w-[15%] pl-0 lg:pl-8">
              {isScrolled && (
                <img 
                  src={Logo}
                  alt="Nirvana Career Counselling - Expert Guidance in Bangalore, Jamshedpur, and Ranchi"
                  loading="lazy" 
                  className="h-16 lg:h-20 object-contain mt-2"
                />
              )}
            </div>
            
            <div className="hidden lg:flex flex-grow justify-center items-center animate-pulseSlow">
              <div className={`inline-block px-8 text-black ml-16 lg:ml-24 text-lg font-bold rounded-md transition-all duration-300 transform ${
                isScrolled ? 'bg-teal-600' : 'bg-white'
              }`}>
                <h1 
                  className={`text-2xl font-bold font-lato text-center mb-2 mt-2 ${
                    isScrolled ? 'text-white' : 'text-teal-700'
                  }`}
                >
                  Your First Session with Us is 
                  <span className="ml-2 text-4xl font-bold inline-block text-red-600 drop-shadow-outline-white">
                    FREE!
                  </span>
                </h1>
                <p className={`text-sm font-normal text-center mb-2 ${
                  isScrolled ? 'text-yellow-200' : 'text-teal-600'
                }`}>
                  📍 Available in Bangalore, Jamshedpur & Ranchi
                </p>
              </div>
            </div>

            {/* Contact Info */}
            <div className="hidden lg:flex lg:w-[22%] justify-end items-center pl-12">
              <button 
                onClick={() => setIsBookingOpen(true)} 
                className={`px-6 py-2 rounded-md transition-all duration-300 ${
                  isScrolled 
                    ? 'bg-teal-600 text-white font-bold hover:bg-yellow-500 hover:text-black' 
                    : 'bg-white text-teal-700 font-bold hover:bg-amber-600 hover:text-white'
                }`}
              >
                Contact Us
              </button>
            </div>

            {/* Mobile FREE Message */}
            <div className="lg:hidden flex-grow mx-4 animate-pulseSoft">
              <div className={`text-center px-2 py-1 rounded ${
                isScrolled ? 'bg-teal-600' : 'bg-white'
              }`}>
                <h1 className={`text-lg font-bold ${
                  isScrolled ? 'text-white' : 'text-teal-700'
                }`}>
                  First Session
                  <span className={`ml-1 text-xl font-bold inline-block drop-shadow-outline-white ${
                    isScrolled ? 'text-red-600' : 'text-red-600'
                  }`}>
                    FREE!
                  </span>
                </h1>
                <p className={`text-xs ${
                  isScrolled ? 'text-yellow-200' : 'text-teal-600'
                }`}>
                  JH • KA • Online
                </p>
              </div>
            </div>

            {/* Contact Buttons */}
            <div className="flex items-center">
              <button 
                onClick={() => setIsBookingOpen(true)}
                className={`lg:hidden p-2 rounded-md transition-all duration-300 ${
                  isScrolled ? 'bg-teal-600 text-white' : 'bg-white text-teal-600'
                }`}
              >
                <Phone className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>

      {/* Hero Section */}
      <main role="main">
      <section
        aria-label='Hero Section'
        className="hero-parallax relative flex items-center"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(8, 64, 59, 0.9), rgba(13, 148, 136, 0.8)), url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '100vh'
        }}
      >
        <div className="max-w-9xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:mt-12 lg:mt-24">
          <div className="relative z-10">
            {/* Logo and Main Content */}
            <div className="text-center mb-8">
              <img 
                src={Logo}
                alt="Nirvana Career Counselling Logo"
                className="mx-auto mb-8 w-40 h-40 lg:w-60 lg:h-60 object-contain"
              />
              <h1 className="text-3xl md:text-5xl font-lato font-black mb-4 lg:mb-6 text-white drop-shadow-lg px-2">
                Nirvana Career Counselling
              </h1>
              {/* Mobile heading */}
              <h2 className="text-2xl md:hidden font-lato font-black mx-auto text-yellow-400 drop-shadow-lg italic px-4">
                Unlock Your Potential
              </h2>
              {/* Desktop heading */}
              <h2 className="hidden md:block text-3xl md:text-4xl font-lato font-black max-w-6xl mx-auto text-yellow-400 drop-shadow-lg italic">
                Unlock Your Potential and Head Towards Success
              </h2>
            </div>

            {/* Updated Description with Multi-Location Focus */}
            <div className="text-center">
              <p className="text-lg md:text-2xl font-lato font-light mb-4 lg:mb-6 max-w-4xl mx-auto text-white drop-shadow-md px-4">
                Expert Career Guidance for Students and Professionals
              </p>
              <p className="text-base md:text-xl font-lato font-medium mb-8 lg:mb-12 max-w-4xl mx-auto text-yellow-300 drop-shadow-md px-4">
                📍 Serving Bangalore, Jamshedpur & Ranchi | 🌐 Online Consultations Available
              </p>
              
              {/* Location Highlights for Mobile */}
              <div className="md:hidden flex justify-center space-x-4 mb-6 px-4">
                <span className="bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-3 py-1 text-white text-sm">
                  Bangalore
                </span>
                <span className="bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-3 py-1 text-white text-sm">
                  Ranchi
                </span>
                <span className="bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-3 py-1 text-white text-sm">
                  Jamshedpur
                </span>
              </div>

              {/* Location Highlights for Desktop */}
              <div className="hidden md:flex justify-center space-x-6 mb-8">
                <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg px-4 py-2 text-white">
                  <div className="font-semibold">Bangalore</div>
                </div>
                <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg px-4 py-2 text-white">
                  <div className="font-semibold">Jamshedpur</div>
                </div>
                <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg px-4 py-2 text-white">
                  <div className="font-semibold">Ranchi</div>
                </div>
              </div>

              <div className="space-y-4 md:space-y-0 md:space-x-6 mt-4 animate-pulseSlow px-4">
                <button 
                  onClick={() => setIsBookingOpen(true)} 
                  className="w-full md:w-auto inline-block px-8 py-3 md:px-12 md:py-4 bg-yellow-500 text-black text-lg font-bold rounded-md hover:bg-amber-600 transition-all duration-300 hover:shadow-lg hover:text-white transform hover:-translate-y-0.5"
                >
                  Book Your Free Session
                </button>
              </div>
              
              {/* Additional Trust Indicator */}
              <div className="mt-6 text-center">
                <p className="text-white text-sm opacity-90">
                  ⭐ 4.9/5 Rating | 🎓 30+ Years Experience | 🔒 100% Confidential
                </p>
              </div>
            </div>
          </div>
          
          {/* Scroll Down Animation */}
          <div className={`absolute bottom-8 left-1/2 transform -translate-x-1/2 transition-opacity duration-500 ${
            isScrolled ? 'opacity-0' : 'opacity-70'
          }`}>
            <div className="flex flex-col items-center">
              <svg 
                className="w-6 h-6 text-white animate-pulse" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M19 14l-7 7m0 0l-7-7m7 7V3" 
                />
              </svg>
            </div>
          </div>
        </div>
      </section>

{/* Replace the existing Psychometric Assessments section with: */}
<section aria-label="Psychometric Assessments" className="py-20 bg-white font-lato">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    {/* Section Header */}
    <div className="text-center mb-16">
      <h2 className="text-4xl font-bold text-gray-900 mb-4">Psychometric Career Assessments</h2>
      <div className="w-24 h-1 bg-teal-600 mx-auto rounded-full mb-8"></div>
      <p className="text-gray-600 max-w-3xl mx-auto px-4">
        Discover your perfect career path with our comprehensive assessment tools designed for students and professionals at every stage.
      </p>
    </div>

    {/* First Row - 4 Cards */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
      {/* Card 1 */}
      <div className="bg-white rounded-lg shadow-md p-6 transform transition duration-300 hover:scale-105 hover:shadow-xl flex flex-col justify-between border-2 border-teal-300">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
            <BookOpen className="h-8 w-8 text-teal-600" />
          </div>
          <h4 className="text-xl font-semibold text-gray-800 mb-4 flex flex-col items-center">
            <span>Career Analysis</span>
            <span>Classes 2nd to 7th</span>
          </h4>
          <p className="text-gray-600 mb-4 text-center">
            Discover your child's Multiple Intelligence profile and early aptitude indicators.
          </p>
        </div>
        <button
          onClick={() => setIsBookingOpen(true)}
          className="mt-4 w-full px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-yellow-300 hover:text-black transition-colors focus:outline-none focus:ring-2 focus:ring-teal-600"
        >
          Start Assessment
        </button>
      </div>

      {/* Card 2 */}
      <div className="bg-white rounded-lg shadow-md p-6 transform transition duration-300 hover:scale-105 hover:shadow-xl flex flex-col justify-between border-2 border-teal-300">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
            <School className="h-8 w-8 text-teal-600" />
          </div>
          <h4 className="text-xl font-semibold text-gray-800 mb-4 flex flex-col items-center">
            <span>Career Analysis</span>
            <span>Classes 8th-10th</span>
          </h4>
          <p className="text-gray-600 mb-4 text-center">
            Find the most suitable career path and subject combinations for high school success.
          </p>
        </div>
        <button
          onClick={() => setIsBookingOpen(true)}
          className="mt-4 w-full px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-yellow-300 hover:text-black transition-colors focus:outline-none focus:ring-2 focus:ring-teal-600"
        >
          Start Assessment
        </button>
      </div>

      {/* Card 3 */}
      <div className="bg-white rounded-lg shadow-md p-6 transform transition duration-300 hover:scale-105 hover:shadow-xl flex flex-col justify-between border-2 border-teal-300">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
            <School2 className="h-8 w-8 text-teal-600" />
          </div>
          <h4 className="text-xl font-semibold text-gray-800 mb-4 flex flex-col items-center">
            <span>Career Analysis</span>
            <span>Classes 11th-12th</span>
          </h4>
          <p className="text-gray-600 mb-4 text-center">
            Get a detailed career roadmap with college and course recommendations.
          </p>
        </div>
        <button
          onClick={() => setIsBookingOpen(true)}
          className="mt-4 w-full px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-yellow-300 hover:text-black transition-colors focus:outline-none focus:ring-2 focus:ring-teal-600"
        >
          Start Assessment
        </button>
      </div>

      {/* Card 4 */}
      <div className="bg-white rounded-lg shadow-md p-6 transform transition duration-300 hover:scale-105 hover:shadow-xl flex flex-col justify-between border-2 border-teal-300">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
            <Omega className="h-8 w-8 text-teal-600" />
          </div>
          <h4 className="text-xl font-semibold text-gray-800 mb-4 flex flex-col items-center">
            <span>Career Analysis</span>
            <span>Graduates</span>
          </h4>
          <p className="text-gray-600 mb-4 text-center">
          Plan your post-graduation career path with detailed insights and guidance.
          </p>
        </div>
        <button
          onClick={() => setIsBookingOpen(true)}
          className="mt-4 w-full px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-yellow-300 hover:text-black transition-colors focus:outline-none focus:ring-2 focus:ring-teal-600"
        >
          Start Assessment
        </button>
      </div>
    </div>

    {/* Second Row - 3 Cards (Offset) */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8 max-w-5xl mx-auto">
      {/* Card 5 */}
      <div className="bg-white rounded-lg shadow-md p-6 transform transition duration-300 hover:scale-105 hover:shadow-xl flex flex-col justify-between border-2 border-teal-300">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
            <Handshake className="h-8 w-8 text-teal-600" />
          </div>
          <h4 className="text-xl font-semibold text-gray-800 mb-4">
            Business Management
          </h4>
          <p className="text-gray-600 mb-4 text-center">
            Explore your potential in business and management careers.
          </p>
        </div>
        <button
          onClick={() => setIsBookingOpen(true)}
          className="mt-4 w-full px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-yellow-300 hover:text-black transition-colors focus:outline-none focus:ring-2 focus:ring-teal-600"
        >
          Start Assessment
        </button>
      </div>

      {/* Card 6 */}
      <div className="bg-white rounded-lg shadow-md p-6 transform transition duration-300 hover:scale-105 hover:shadow-xl flex flex-col justify-between border-2 border-teal-300">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
            <GraduationCap className="h-8 w-8 text-teal-600" />
          </div>
          <h4 className="text-xl font-semibold text-gray-800 mb-4 flex flex-col items-center">
            <span>Career Analysis</span>
            <span>Professionals</span>
          </h4>
          <p className="text-gray-600 mb-4 text-center">
            Get guidance for career transitions and professional growth opportunities.
          </p>
        </div>
        <button
          onClick={() => setIsBookingOpen(true)}
          className="mt-4 w-full px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-yellow-300 hover:text-black transition-colors focus:outline-none focus:ring-2 focus:ring-teal-600"
        >
          Start Assessment
        </button>
      </div>

      {/* Card 7 */}
      <div className="bg-white rounded-lg shadow-md p-6 transform transition duration-300 hover:scale-105 hover:shadow-xl flex flex-col justify-between border-2 border-teal-300">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
            <BriefcaseBusiness className="h-8 w-8 text-teal-600" />
          </div>
          <h4 className="text-xl font-semibold text-gray-800 mb-4 flex flex-col items-center">
            Engineering Assessment
          </h4>
          <p className="text-gray-600 mb-4 text-center">
          `Choose the right engineering specialization based on your aptitude and interests.
          </p>
        </div>
        <button
          onClick={() => setIsBookingOpen(true)}
          className="mt-4 w-full px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-yellow-300 hover:text-black transition-colors focus:outline-none focus:ring-2 focus:ring-teal-600"
        >
          Start Assessment
        </button>
      </div>
    </div>
  </div>
</section>

{/* Replace the About The Counsellor section with: */}
<section aria-label='About the Counsellor' className="py-16 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">About The Counsellor</h2>
          <div className="w-24 h-1 bg-teal-600 mx-auto"></div>
        </div>
        
        <div className="bg-teal-600 rounded-2xl shadow-xl p-8 max-w-4xl mx-auto relative">
          {/* Experience Highlights */}
          <div className="absolute -left-4 top-1/4 hidden lg:block">
            <div className="bg-yellow-400 rounded-lg p-4 shadow-lg transform -rotate-6">
              <p className="text-black font-bold text-lg ml-8">50+</p>
              <p className="text-black flex items-center ml-2">
                5 <Star className="text-yellow-600 mr-1 text-sm" /> Reviews
              </p>
            </div> 
          </div>

          <div className="absolute -right-4 top-0.5 hidden lg:block">
            <div className="bg-yellow-400 rounded-lg p-4 shadow-lg transform rotate-6">
              <p className="text-black text-md">Let's find you a</p>
              <p className="text-black text-lg font-bold ml-2">Perfect Fit!</p>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex flex-col lg:flex-row items-center gap-8 mb-8">
            {/* Profile Section */}
            <div className="flex flex-col items-center lg:w-1/3">
              <div className="relative">
                <img 
                  src={ProfilePic}
                  alt="Nirvana Career Counselling - Expert Guidance by Subhransu Dutta"
                  loading="lazy"
                  className="w-40 h-40 rounded-full object-cover border-4 border-white shadow-lg"
                />
                {/* Certification Badge */}
                <div className="absolute -bottom-2 -right-16 bg-white rounded-full p-2 shadow-lg">
                  <img 
                    src="https://www.edumilestones.com/ccablack.png" 
                    alt="Certification badge"
                    className="w-24 h-8"
                  />
                </div>
              </div>
              <h4 className="text-2xl font-semibold text-white mt-7">Subhransu Dutta</h4>
              <p className="text-yellow-300 font-medium">Career Counselling Expert</p>
              
              {/* Expertise Areas */}
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {['Career Guidance', 'Professional Mentoring', 'Student Counselling'].map((skill) => (
                  <span key={skill} className="bg-teal-700 text-white px-3 py-1 rounded-full text-sm">
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            {/* Text Content */}
            <div className="prose prose-lg prose-invert mt-12 lg:w-2/3 text-white">
              <p className="mb-4">
                To be able to guide myself to pursue a career as a counselor made me realize 
                the value the right resources have in a person's life.
              </p>
              <p className="mb-4">
                Having 30 years of experience in the corporate universe and collecting knowledge 
                about the lifestyle over the years would not allow me to pass up the opportunity 
                to guide people in the right direction. Exposure to different leadership roles and 
                tough decisions gave me the confidence to mentor professionals at various levels 
                throughout my corporate career.
              </p>
              <p>
                With all the experience and responsibilities I have had in my life, I am ready to 
                point in the right direction and show students and professionals the path to success. 
                "You only need guidance to lead you in the right direction until you can do it yourself".
              </p>
            </div>
          </div>

          {/* Achievement Tiles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            {[
              { title: 'Corporate Experience', value: '30+ Years' },
              { title: 'Clients Guided', value: '100+' },
              { title: 'Average Rating', value: '4.9/5' }
            ].map((achievement) => (
              <div key={achievement.title} className="bg-teal-700 rounded-lg p-4 text-center">
                <p className="text-white font-bold text-2xl">{achievement.value}</p>
                <p className="text-yellow-400 text-sm">{achievement.title}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

<ReviewsCarousel />

{/* Replace the About Us section with: */}
<section aria-label="About Us" className="py-16 bg-white relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-0 right-0 w-64 h-64 bg-teal-50 rounded-full -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-yellow-50 rounded-full -ml-32 -mb-32"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">About Us</h2>
          <div className="w-24 h-1 bg-teal-600 mx-auto"></div>
        </div>

        {/* Main content card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left side: Vision */}
            <div className="space-y-6">
              <div className="bg-teal-50 rounded-xl p-6">
                <h3 className="text-2xl font-semibold text-teal-800 mb-4 flex items-center">
                  <Target className="w-6 h-6 mr-2" />
                  Our Vision
                </h3>
                <p className="text-gray-700">
                  Nirvana Career Counselling, named after its vision, strives to provide its clients 
                  the utmost happiness and an ideal position in one's career. We aim to be the guiding 
                  light in your career journey.
                </p>
              </div>

              {/* Mission Statement */}
              <div className="bg-yellow-50 rounded-xl p-6">
                <h3 className="text-2xl font-semibold text-yellow-800 mb-4 flex items-center">
                  <Lightbulb className="w-6 h-6 mr-2" />
                  Our Mission
                </h3>
                <p className="text-gray-700">
                  To uncover multiple available and compatible career options before making 
                  the perfect match with the most suited position, structuring prospective 
                  and achievable goals for our clients.
                </p>
              </div>
            </div>

            {/* Right side: Image and stats */}
            <div className="space-y-6">
              <div className="relative">
                <img 
                  src={AboutUsImg}
                  alt="Career counselling session"
                  className="w-full h-80 object-cover rounded-xl shadow-lg"
                />
                {/* Overlay stats */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/100 to-transparent p-4 rounded-b-xl">
                  <div className="grid grid-cols-3 gap-4 text-white text-center mt-4">
                    <div>
                      <p className="text-2xl font-bold">100+</p>
                      <p className="text-sm">Success Stories</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">4.9/5</p>
                      <p className="text-sm flex items-center lg:ml-10">Average Rating</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">30+</p>
                      <p className="text-sm">Years of Experience</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Value Propositions */}
        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              icon: <Users className="w-8 h-8 text-teal-600" />,
              title: "Personalized Approach",
              description: "Every individual is unique, and so is our approach to their career guidance."
            },
            {
              icon: <Award className="w-8 h-8 text-teal-600" />,
              title: "Expert Guidance",
              description: "Backed by years of corporate experience and professional expertise."
            },
            {
              icon: <Target className="w-8 h-8 text-teal-600" />,
              title: "Continuous Support",
              description: "We stay with you until you reach the top of your success ladder."
            }
          ].map((item, index) => (
            <div 
              key={index} 
              className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow"
            >
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                {item.icon}
              </div>
              <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
              <p className="text-gray-600">{item.description}</p>
            </div>
          ))}
        </div>

        {/* Quote */}
        <div className="mt-16 text-center">
          <blockquote className="text-xl italic text-teal-700">
            "You only need guidance to lead you in the right direction until you can do it yourself"
          </blockquote>
        </div>
      </div>
    </section>

<section aria-label='Frequently Asked Questions'>
  <FAQSection />
</section>
</main>

      {/* Footer */}
      <footer role="contentinfo" className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Footer Content */}
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="flex items-center mb-4">
                <img 
                  src={Logo}
                  alt="Nirvana Career Counselling Logo"
                  className="h-12 w-12 mr-3"
                />
                <h4 className="text-xl font-semibold">Nirvana Career Counselling</h4>
              </div>
              <p className="text-gray-300 mb-4 leading-relaxed">
                Expert career guidance serving students and professionals across Jharkhand and Karnataka. 
                Specialized counselling for Jamshedpur's industrial sector, Ranchi's diverse opportunities, 
                and Bangalore's tech ecosystem.
              </p>
              <div className="flex items-center space-x-4">
                <div className="bg-teal-600 rounded-full p-2">
                  <Phone className="h-4 w-4" />
                </div>
                <span className="text-gray-300">+91 9731166822</span>
              </div>
            </div>

            {/* Service Areas */}
            <div>
              <h5 className="text-lg font-semibold mb-4 text-teal-400">Service Areas</h5>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-center">
                  <MapPin className="h-4 w-4 mr-2 text-teal-400" />
                  <span>Bangalore, Karnataka</span>
                </li>
                <li className="flex items-center">
                  <MapPin className="h-4 w-4 mr-2 text-teal-400" />
                  <span>Jamshedpur, Jharkhand</span>
                </li>
                <li className="flex items-center">
                  <MapPin className="h-4 w-4 mr-2 text-teal-400" />
                  <span>Ranchi, Jharkhand</span>
                </li>
                <li className="flex items-center">
                  <Globe className="h-4 w-4 mr-2 text-teal-400" />
                  <span>Online Consultations</span>
                </li>
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h5 className="text-lg font-semibold mb-4 text-teal-400">Quick Links</h5>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/blog" className="hover:text-teal-400 transition-colors">Career Blog</Link></li>
                <li><Link to="/jamshedpur" className="hover:text-teal-400 transition-colors">Jamshedpur Counselling</Link></li>
                <li><Link to="/ranchi" className="hover:text-teal-400 transition-colors">Ranchi Counselling</Link></li>
                <li>Psychometric Testing</li>
                <li>Industry-Specific Advice</li>
              </ul>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="border-t border-gray-700 pt-6 text-center">
            <div className="mb-4">
              <h4 className="text-xl font-semibold mb-2">Copyright © Nirvana Career Counselling</h4>
              <p className="text-gray-300 mb-2">
                📍 Proudly Serving Bangalore, Jamshedpur & Ranchi
              </p>
              <p className="text-sm text-gray-400">
                Jharkhand • Karnataka • Online Counselling Available Nationwide
              </p>
            </div>
            <div className="text-sm text-gray-400">
              <p>Thigalarapalya, Whitefield, Bangalore, Karnataka - 560048</p>
              <p className="mt-2">
                🌟 Rated 4.9/5 • 🎓 30+ Years Experience • 🔒 100% Confidential
              </p>
            </div>
          </div>
        </div>
      </footer>
      {/* Sticky Mobile CTA */}
      {isScrolled && (
        <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-teal-700 shadow-2xl border-t border-teal-500">
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <p className="text-white text-sm font-semibold">Book Your Free Session</p>
              <p className="text-yellow-300 text-xs">Online • Bangalore • Jamshedpur • Ranchi</p>
            </div>
            <button
              onClick={() => setIsBookingOpen(true)}
              className="bg-yellow-500 text-black font-bold px-4 py-2 rounded-md text-sm hover:bg-yellow-400"
            >
              Book Now →
            </button>
          </div>
        </div>
      )}

      {isBookingOpen && (
      <BookingPopup onClose={() => setIsBookingOpen(false)} />
    )}
    </div>
    </>
  );
};